import React from "react"
import { useAuth } from "@clerk/clerk-react"
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query"
import Router from "./components/Router"
import UserProvider from "./components/providers/UserContext"
import { UserContext } from "./context"
import { useClerkAuth } from "./hooks/useClerkAuth"
import { useRefinedAccess } from "./hooks/useRefinedAccess"
import {
  clearAccountSession,
  selectOrganization,
  selectedOrganization,
  setRequestOrganization,
} from "./utils/account-session"
import { isPublicRoute } from "./utils/routes"
import { useLocation } from "react-router-dom"
import * as Sentry from "@sentry/react"

function AccountApp() {
  const { setLoggedInUser } = React.useContext(UserContext)
  const { getToken, userId } = useAuth()
  const location = useLocation()
  // QR/login/privacy routes need no probe. UserInfo independently probes /me
  // because public profiles also expose authenticated administrative controls.
  const isPublic = isPublicRoute(location.pathname)
  const isInvitation = location.pathname === "/learner-invitation"
  const enabled = !isPublic && !isInvitation
  const { account } = useRefinedAccess(enabled)
  // A failed token bridge belongs to this authority, not a different one: a
  // non-authority /me content delta preserves it until the token settles again.
  const [tokenError, setTokenError] = React.useState<string | undefined>()
  const client = useQueryClient()
  // Authority changes must clear private state even while viewing a public
  // profile. Navigation alone must not change the account scope.
  const scope = account
    ? `${account.accountId}:${account.organizationId}:${account.roles.join(",")}:${account.isSuperAdmin}`
    : "public"
  const [readyScope, setReadyScope] = React.useState(scope)
  // The protected <Router> hosts the /me query observer. Unmounting it on the
  // first public -> <account> establishment would remount the observer and,
  // with useRefinedAccess's staleTime:0 + default refetchOnMount, refetch /me
  // again; a transient 403 on that redundant refetch could bounce a valid user
  // to <AccessDenied />. Only unmount on a genuine in-session authority
  // change, not the initial establishment (which has no stale UI to tear down
  // and no non-/me cache yet to clear).
  const established = React.useRef(false)
  React.useLayoutEffect(() => {
    if (account && userId) {
      // Pin even a backend-selected fallback org before mounting admin consumers.
      selectOrganization(userId, account.organizationId)
      setRequestOrganization(account.organizationId)
    }
    if (scope !== readyScope) {
      // A backend-selected default organization or role can also change on refresh.
      // Unmount the old UI before clearing all non-authority server state.
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" })
      client.getMutationCache().clear()
      setReadyScope(scope)
    }
    if (account) established.current = true
  }, [account, userId, client, scope, readyScope])
  // The token bridge is rebuilt only on an authority (scope) change, not on
  // every /me content delta. useRefinedAccess sets staleTime:0 and inherits
  // refetchOnWindowFocus, so a tab refocus yields a fresh `account` reference
  // for any content delta (email, memberships, employeeId); keying this effect
  // on `account` would tear down loggedInUser and re-run getToken() even when the
  // admin's authority was unchanged, collapsing profileReady (which waits on
  // loggedInUser?.token) and blanking an already-loaded profile for the duration
  // of the token round-trip. `accountRef` reads the latest account fields when
  // the effect does run, so the rebuilt session reflects the most recent /me
  // content for that authority.
  const accountRef = React.useRef(account)
  accountRef.current = account
  React.useEffect(() => {
    let cancelled = false
    const acct = accountRef.current
    setLoggedInUser(undefined)
    if (!acct) {
      setTokenError(undefined)
      return
    }
    // Establishing a (possibly new) authority: clear any error from a prior
    // authority. The effect does not re-run on a non-authority /me content
    // delta (scope unchanged), so a same-authority token error is preserved.
    setTokenError(undefined)
    getToken()
      .then((token) => {
        if (cancelled) return
        if (!token) throw new Error("No active session")
        setLoggedInUser({
          email: acct.email,
          password: "",
          token,
          isAdmin: acct.isAdmin,
          isSuperAdmin: acct.isSuperAdmin,
          organizationId: acct.organizationId,
          roles: acct.roles,
        })
      })
      .catch((error) => {
        if (cancelled) return
        Sentry.captureException(error)
        setTokenError(scope)
      })
    return () => {
      cancelled = true
    }
  }, [scope, getToken, setLoggedInUser])
  if (!isPublic && !isInvitation && scope !== readyScope && established.current) return null
  return (
    <React.Suspense fallback={null}>
      <Router sessionError={!!account && tokenError === scope} scope={scope} />
    </React.Suspense>
  )
}

function AccountScope() {
  const [client] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { staleTime: 5 * 60 * 1000, gcTime: 30 * 60 * 1000, retry: 1 } },
      }),
  )
  React.useEffect(
    () => () => {
      client.clear()
    },
    [client],
  )
  return (
    <QueryClientProvider client={client}>
      <UserProvider>
        <AccountApp />
      </UserProvider>
    </QueryClientProvider>
  )
}

export default function App() {
  const { userId, isLoaded } = useAuth()
  const previousUser = React.useRef(userId)
  React.useEffect(() => {
    if (isLoaded && previousUser.current && previousUser.current !== userId)
      clearAccountSession(previousUser.current)
    if (isLoaded) previousUser.current = userId
  }, [userId, isLoaded])
  useClerkAuth()
  const organization = userId ? selectedOrganization(userId) : undefined
  setRequestOrganization(organization)
  if (!isLoaded) return null
  // Organization changes clear state inside AccountApp; explicit acceptance reloads the app.
  return <AccountScope key={userId ?? "public"} />
}
